<template>
  <div class="rates-tariff-modal">
    <nav class="rates-tariff-modal__nav" aria-label="Тарифы">
      <button
        v-for="tariff in tariffs"
        :key="tariff.CODE"
        type="button"
        class="rates-tariff-modal__nav-button"
        :class="{ 'is-active': tariff.CODE === activeTariff?.CODE }"
        @click="selectTariff(tariff.CODE)"
      >
        {{ tariff.NAME }}
      </button>
    </nav>

    <div
      ref="scrollContainer"
      class="rates-tariff-modal__scroll"
      @click="handleContentClick"
    >
      <div v-if="activeTariff" class="rates-tariff-modal__pane grid-2024">
        <div
          class="rates-tariff-modal__container g-col-full g-start-ld-2 g-col-ld-6 g-start-lg-4 g-col-lg-6"
        >
          <div
            v-if="activeTariff.PREVIEW_TEXT"
            class="rates-tariff-modal__description t2"
            v-html="activeTariff.PREVIEW_TEXT"
          />

          <div class="rates-tariff-modal__conditions">
            <p v-if="activeTariff.DESC" class="t1" v-html="activeTariff.DESC" />
            <div v-if="activeTariff.START_SUM" class="rates-tariff-modal__price h2">
              <span>{{ activeTariff.START_SUM }}</span>
              <sup class="t3">{{ activeTariff.SIGNS.join(' ') }}</sup>
            </div>
          </div>

          <div class="rates-tariff-modal__sections">
            <section
              v-for="section in activeTariff.SECTIONS"
              :key="section.CODE"
              class="rates-tariff-modal__section"
              :class="`-${section.CODE}`"
            >
              <h3
                v-if="section.NAME"
                class="rates-tariff-modal__section-title h3"
                v-html="section.NAME"
              />

              <div
                v-for="subsection in section.SUBSECTIONS"
                :key="subsection.CODE"
                class="rates-tariff-modal__features"
                :class="`-${subsection.CODE}`"
              >
                <p
                  v-if="subsection.NAME"
                  class="rates-tariff-modal__features-title"
                  v-html="subsection.NAME"
                />

                <template v-for="field in subsection.FIELDS" :key="field.CODE">
                  <div
                    v-if="field.NAME"
                    class="rates-tariff-modal__feature-name"
                    :class="{ hidden: isHiddenField(field) }"
                    v-html="field.NAME"
                  />
                  <template
                    v-for="(value, index) in field.VALUES"
                    :key="`${field.CODE}-${index}`"
                  >
                    <div
                      v-if="value.FORMAT_VALUE"
                      class="rates-tariff-modal__feature-value"
                      :class="{ hidden: isHiddenField(field) }"
                      v-html="value.FORMAT_VALUE"
                    />
                  </template>
                </template>
              </div>
            </section>

            <div
              v-if="activeTariff.DETAIL_TEXT"
              class="rates-tariff-modal__features -extra"
            >
              <p
                v-if="activeTariff.DETAIL_TEXT_TITLE"
                class="rates-tariff-modal__features-title"
                v-html="activeTariff.DETAIL_TEXT_TITLE"
              />
              <div
                class="rates-tariff-modal__features-content"
                v-html="activeTariff.DETAIL_TEXT"
              />
            </div>

            <div class="rates-tariff-modal__support">
              <p>
                Ознакомиться с полным перечнем комиссий можно по&nbsp;
                <a href="/support/documents/customer-regulating/">ссылке</a>
              </p>
            </div>
          </div>

          <div class="rates-tariff-modal__buttons">
            <Button href="/aos/aton/" @click="trackOpenAccount"> Открыть счет </Button>
            <Button href="#consultation" @click="emit('close')"> Консультация </Button>
          </div>

          <div
            v-if="activeTariff.FOOTER_TEXT"
            class="rates-tariff-modal__footnotes"
            v-html="activeTariff.FOOTER_TEXT"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { nextTick, ref } from 'vue';
  import { Button } from '@nuxt-shared/ui/button';
  import type { TariffField, TariffItem } from '../types';

  defineProps<{
    tariffs: TariffItem[];
    activeTariff: TariffItem | null;
  }>();

  const emit = defineEmits<{
    select: [code: string];
    close: [];
  }>();

  const scrollContainer = ref<HTMLElement | null>(null);

  const isHiddenField = (field: TariffField): boolean =>
    field.CODE === 'InvestmentConsulting' && field.VALUES[0]?.FORMAT_VALUE === '—';

  const selectTariff = async (code: string) => {
    emit('select', code);
    await nextTick();
    scrollContainer.value?.scrollTo({ top: 0 });
  };

  const trackOpenAccount = () => {
    if (typeof window === 'undefined') return;
    const ym = (window as typeof window & { ym?: (...args: unknown[]) => void }).ym;
    ym?.(28725931, 'reachGoal', 'OpenFormOpenAccount');
  };

  const handleContentClick = (event: MouseEvent) => {
    const element = event.target instanceof Element ? event.target : null;
    const anchor = element?.closest<HTMLAnchorElement>('a[href*="#tariff_footnote_"]');
    const sup = element?.closest<HTMLElement>('.rates-tariff-modal__sections sup');
    const footnoteNumber = sup?.textContent?.trim() ?? '';
    const selector =
      anchor?.hash ||
      sup?.dataset.href ||
      (/^\d+$/.test(footnoteNumber) ? `#tariff_footnote_${footnoteNumber}` : '');

    if (!selector || !scrollContainer.value) return;
    const target = scrollContainer.value.querySelector<HTMLElement>(selector);
    if (!target) return;

    event.preventDefault();
    const containerRect = scrollContainer.value.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    scrollContainer.value.scrollTo({
      top: scrollContainer.value.scrollTop + targetRect.top - containerRect.top,
      behavior: 'smooth',
    });
  };
</script>
